import { useState } from "react";
import { apiFetch } from "../utils/api";

interface AdaptiveFormData {
  // Shared base fields (universal analytics)
  name: string;
  email: string;
  phone: string;
  organizationType: string;
  service: string;

  // Training-specific fields
  trainingCategory?: string;
  participantCount?: string;
  trainingMode?: string;

  // IT & SaaS fields
  itRequirement?: string;
  platformType?: string;
  budgetRange?: string;
  timeline?: string;

  // R&D & Hardware fields
  rndDomain?: string;
  hardwareStage?: string;

  // Robotics fields
  automationTarget?: string;
  operatingEnvironment?: string;

  // Security fields
  securityNeed?: string;
  facilityScale?: string;

  // Custom field
  customScopeCategory?: string;

  // Shared message
  message: string;
}

const initialForm: AdaptiveFormData = {
  name: "",
  email: "",
  phone: "",
  organizationType: "Startup / Small Business",
  service: "IT & ITeS Services",
  itRequirement: "Custom Software Development",
  budgetRange: "₹25,000 – ₹50,000 (Standard)",
  timeline: "1 Month",
  trainingCategory: "Python Programming & Automation",
  participantCount: "Individual (1 Person)",
  trainingMode: "Online Live Interactive",
  platformType: "Multi-Tenant SaaS Product",
  rndDomain: "Applied Hardware Prototyping",
  hardwareStage: "Concept & Feasibility Study",
  automationTarget: "Intelligent Robotics Assembly",
  operatingEnvironment: "Commercial Facility",
  securityNeed: "IP Surveillance & CCTV System",
  facilityScale: "Small Office / Retail",
  customScopeCategory: "Custom Hardware + Software Hybrid",
  message: "",
};

export function ContactForm() {
  const [form, setForm] = useState<AdaptiveFormData>(initialForm);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const update = (field: keyof AdaptiveFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;

    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await apiFetch("/api/contact", {
        method: "POST",
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to send message");
      }

      setStatus("sent");
      setForm(initialForm);
    } catch (err: any) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  // Helper to check selected service category
  const isTraining = form.service === "Technology Training & Education";
  const isIT = form.service === "IT & ITeS Services";
  const isSaaS = form.service === "SaaS & Digital Platforms";
  const isRnD = form.service === "Research & Development (Hardware/Software)";
  const isRobotics = form.service === "Robotics & Automation Systems";
  const isSecurity = form.service === "Security & Surveillance Systems";
  const isCustom = form.service === "Custom Engineering Requirement";

  return (
    <section id="contact" className="no-print bg-gradient-to-br from-brand-dark via-brand to-brand-dark py-16 sm:py-20 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: "radial-gradient(circle at 30% 50%, #0ea5e9 0%, transparent 50%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
          {/* Left - Info Panel */}
          <div>
            <p className="text-brand-light font-semibold text-sm uppercase tracking-wider mb-2">Get In Touch</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-4">
              Discuss your next project requirement.
            </h2>
            <p className="text-slate-200 leading-relaxed mb-8 max-w-xl">
              Whether you need software development, technical training, hardware prototyping, or security solutions, share your details and our team will be in touch.
            </p>

            <div className="space-y-6 text-slate-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0 border border-white/20">
                  <svg className="w-5 h-5 text-brand-light shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">Email Us</h3>
                  <p>
                    <a href="mailto:connect@radiece.com" aria-label="Send email to connect@radiece.com" className="hover:text-brand-light transition-colors underline underline-offset-2">connect@radiece.com</a>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0 border border-white/20">
                  <svg className="w-5 h-5 text-brand-light shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">Registered Office</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">Building No. 24/1701, Suite No. 1034, Heiley Offices, 2nd Floor KC Arcade, Near TV Centre, CSEZ P.O, Kakkanad, Ernakulam - 682037</p>
                  <p className="mt-1 text-xs text-slate-400">For correspondence purposes only. Please contact us by email to arrange a meeting.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Adaptive Contact Form */}
          <div className="bg-[#101c2a] rounded-2xl p-6 sm:p-8 border border-white/20 shadow-xl">
            {status === "sent" ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-12">
                <div className="w-16 h-16 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mb-2">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">Inquiry Received!</h3>
                <p className="text-slate-300 max-w-sm text-sm leading-relaxed">
                  Thank you for contacting us. Our team will review your requirements and get back to you shortly.
                </p>
                <button onClick={() => setStatus("idle")} className="text-brand-light hover:text-white font-medium text-sm transition-colors underline pt-2">
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" aria-label="Contact and requirement form">
                {/* 1. Shared Fields: Name & Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-slate-200 mb-1.5">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      autoComplete="name"
                      value={form.name}
                      onChange={update("name")}
                      className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-slate-200 mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      autoComplete="email"
                      value={form.email}
                      onChange={update("email")}
                      className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                {/* 2. Shared Fields: Phone & Organization Type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-slate-200 mb-1.5">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="contact-phone"
                      name="phone"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm"
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-org" className="block text-sm font-medium text-slate-200 mb-1.5">
                      Organization Type
                    </label>
                    <select
                      id="contact-org"
                      name="organizationType"
                      value={form.organizationType}
                      onChange={update("organizationType")}
                      className="w-full px-4 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm"
                    >
                      <option value="Startup / Small Business">Startup / Small Business</option>
                      <option value="SME / Growing Business">SME / Growing Business</option>
                      <option value="Enterprise / Corporate">Enterprise / Corporate</option>
                      <option value="Educational Institution / University">Educational Institution / University</option>
                      <option value="Government / PSU">Government / PSU</option>
                      <option value="Individual / Independent Learner">Individual / Independent Learner</option>
                    </select>
                  </div>
                </div>

                {/* 3. Primary Service Area */}
                <div>
                  <label htmlFor="contact-service" className="block text-sm font-medium text-slate-200 mb-1.5">
                    Service Area *
                  </label>
                  <select
                    id="contact-service"
                    name="service"
                    value={form.service}
                    onChange={update("service")}
                    className="w-full px-4 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm font-medium"
                  >
                    <option value="Technology Training & Education">Technology Training & Education</option>
                    <option value="IT & ITeS Services">IT & ITeS Services</option>
                    <option value="SaaS & Digital Platforms">SaaS & Digital Platforms</option>
                    <option value="Research & Development (Hardware/Software)">Research & Development</option>
                    <option value="Robotics & Automation Systems">Robotics & Automation</option>
                    <option value="Security & Surveillance Systems">Security & Surveillance</option>
                    <option value="Custom Engineering Requirement">Custom Engineering Requirement</option>
                  </select>
                </div>

                {/* 4. Contextual Field Sets based on selected service */}

                {/* Training Details */}
                {isTraining && (
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl space-y-4 animate-fadeIn">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-light">
                      Course Details
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="tr-cat" className="block text-xs font-medium text-slate-200 mb-1">
                          Select Course *
                        </label>
                        <select
                          id="tr-cat"
                          value={form.trainingCategory}
                          onChange={update("trainingCategory")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="Python Full-Stack Web Development">Python Full-Stack Web Development</option>
                          <option value="MERN Stack Web Development">MERN Stack Web Development</option>
                          <option value="MEAN Stack Web Development">MEAN Stack Web Development</option>
                          <option value="Software Testing & Quality Assurance">Software Testing & Quality Assurance (Manual & Automated)</option>
                          <option value="Python Programming & Automation">Python Programming & Automation</option>
                          <option value="Data Science & Machine Learning">Data Science & Machine Learning</option>
                          <option value="Modern React & Next.js">Modern React & Next.js</option>
                          <option value="Full-Stack Web Engineering">Full-Stack Web Engineering</option>
                          <option value="Performance Digital Marketing">Performance Digital Marketing</option>
                          <option value="AI & Prompt Engineering">AI & Prompt Engineering</option>
                          <option value="Cyber Security & Ethical Hacking">Cyber Security & Ethical Hacking</option>
                          <option value="Embedded Systems & IoT Engineering">Embedded Systems & IoT Engineering</option>
                          <option value="Custom / Unlisted Subject (Specify in Message)">Custom / Unlisted Subject (Specify in Message)</option>
                          <option value="Corporate Technology Training">Corporate Technology Training</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="tr-mode" className="block text-xs font-medium text-slate-200 mb-1">
                          Learning Mode
                        </label>
                        <select
                          id="tr-mode"
                          value={form.trainingMode}
                          onChange={update("trainingMode")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="Online Live Interactive">Online Live Interactive</option>
                          <option value="Classroom / In-Person">Classroom / In-Person</option>
                          <option value="Self-Paced / Hybrid">Self-Paced / Hybrid</option>
                          <option value="Corporate On-Site Training">Corporate On-Site Training</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="tr-count" className="block text-xs font-medium text-slate-200 mb-1">
                        Number of Participants
                      </label>
                      <select
                        id="tr-count"
                        value={form.participantCount}
                        onChange={update("participantCount")}
                        className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                      >
                        <option value="Individual (1 Person)">Individual (1 Person)</option>
                        <option value="Small Group (2-5 Persons)">Small Group (2-5 Persons)</option>
                        <option value="Team / Batch (6-15 Persons)">Team / Batch (6-15 Persons)</option>
                        <option value="Corporate / University (15+ Persons)">Corporate / University (15+ Persons)</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* IT Details */}
                {isIT && (
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl space-y-4 animate-fadeIn">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-light">
                      Project Scope
                    </p>
                    <div>
                      <label htmlFor="it-req" className="block text-xs font-medium text-slate-200 mb-1">
                        Main Requirement
                      </label>
                      <select
                        id="it-req"
                        value={form.itRequirement}
                        onChange={update("itRequirement")}
                        className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                      >
                        <option value="Custom Software Development">Custom Software Development</option>
                        <option value="Systems Architecture & Consultancy">Systems Architecture & Consultancy</option>
                        <option value="API Integration & Microservices">API Integration & Microservices</option>
                        <option value="Network Infrastructure Setup">Network Infrastructure Setup</option>
                        <option value="System Diagnostics & AMC Contract">System Diagnostics & AMC Contract</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="it-budget" className="block text-xs font-medium text-slate-200 mb-1">
                          Estimated Budget
                        </label>
                        <select
                          id="it-budget"
                          value={form.budgetRange}
                          onChange={update("budgetRange")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="< ₹25,000 (Micro / Starter)">&lt; ₹25,000 (Micro / Starter)</option>
                          <option value="₹25,000 – ₹50,000 (Standard)">₹25,000 – ₹50,000 (Standard)</option>
                          <option value="₹50,000 – ₹1,00,000 (Professional)">₹50,000 – ₹1,00,000 (Professional)</option>
                          <option value="₹1,00,000 – ₹2,50,000 (Business)">₹1,00,000 – ₹2,50,000 (Business)</option>
                          <option value="₹2,50,000+ (Enterprise)">₹2,50,000+ (Enterprise)</option>
                          <option value="To Be Determined / Open">To Be Determined / Open</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="it-time" className="block text-xs font-medium text-slate-200 mb-1">
                          Target Timeline
                        </label>
                        <select
                          id="it-time"
                          value={form.timeline}
                          onChange={update("timeline")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="Immediate (< 2 weeks)">Immediate (&lt; 2 weeks)</option>
                          <option value="1 Month">1 Month</option>
                          <option value="1 – 3 Months">1 – 3 Months</option>
                          <option value="Flexible / Planning Phase">Flexible / Planning Phase</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* SaaS Details */}
                {isSaaS && (
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl space-y-4 animate-fadeIn">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-light">
                      Platform Details
                    </p>
                    <div>
                      <label htmlFor="saas-type" className="block text-xs font-medium text-slate-200 mb-1">
                        Platform Requirement Type
                      </label>
                      <select
                        id="saas-type"
                        value={form.platformType}
                        onChange={update("platformType")}
                        className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                      >
                        <option value="Multi-Tenant SaaS Product">Multi-Tenant SaaS Product</option>
                        <option value="CMS-Driven Web Application">CMS-Driven Web Application</option>
                        <option value="E-Commerce Storefront">E-Commerce Storefront</option>
                        <option value="Mobile App Backend API">Mobile App Backend API</option>
                        <option value="Custom Web Portal">Custom Web Portal</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="saas-budget" className="block text-xs font-medium text-slate-200 mb-1">
                          Estimated Budget
                        </label>
                        <select
                          id="saas-budget"
                          value={form.budgetRange}
                          onChange={update("budgetRange")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="₹25,000 – ₹50,000">₹25,000 – ₹50,000</option>
                          <option value="₹50,000 – ₹1,00,000">₹50,000 – ₹1,00,000</option>
                          <option value="₹1,00,000 – ₹2,50,000">₹1,00,000 – ₹2,50,000</option>
                          <option value="₹2,50,000+ (Enterprise)">₹2,50,000+ (Enterprise)</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="saas-time" className="block text-xs font-medium text-slate-200 mb-1">
                          Target Timeline
                        </label>
                        <select
                          id="saas-time"
                          value={form.timeline}
                          onChange={update("timeline")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="1 Month">1 Month</option>
                          <option value="1 – 3 Months">1 – 3 Months</option>
                          <option value="3 – 6 Months">3 – 6 Months</option>
                          <option value="Planning Phase">Planning Phase</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* R&D Details */}
                {isRnD && (
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl space-y-4 animate-fadeIn">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-light">
                      R&D Details
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="rnd-domain" className="block text-xs font-medium text-slate-200 mb-1">
                          Focus Area
                        </label>
                        <select
                          id="rnd-domain"
                          value={form.rndDomain}
                          onChange={update("rndDomain")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="Applied Hardware Prototyping">Applied Hardware Prototyping</option>
                          <option value="Embedded Microcontroller Firmware">Embedded Microcontroller Firmware</option>
                          <option value="Sensor Integration & Signal Processing">Sensor Integration & Signals</option>
                          <option value="Circuit PCB Layout Design">Circuit PCB Layout Design</option>
                          <option value="Specialized R&D Infrastructure">Specialized R&D Infrastructure</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="rnd-stage" className="block text-xs font-medium text-slate-200 mb-1">
                          Development Stage
                        </label>
                        <select
                          id="rnd-stage"
                          value={form.hardwareStage}
                          onChange={update("hardwareStage")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="Concept & Feasibility Study">Concept & Feasibility Study</option>
                          <option value="Schematic & Circuit Design">Schematic & Circuit Design</option>
                          <option value="Working Prototype Build">Working Prototype Build</option>
                          <option value="Testing & Debugging">Testing & Debugging</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Robotics Details */}
                {isRobotics && (
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl space-y-4 animate-fadeIn">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-light">
                      Automation Details
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="rob-target" className="block text-xs font-medium text-slate-200 mb-1">
                          Automation Focus
                        </label>
                        <select
                          id="rob-target"
                          value={form.automationTarget}
                          onChange={update("automationTarget")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="Intelligent Robotics Assembly">Intelligent Robotics Assembly</option>
                          <option value="Industrial Control Systems">Industrial Control Systems</option>
                          <option value="Microcontroller Firmware">Microcontroller Firmware</option>
                          <option value="Custom Electronics Setup">Custom Electronics Setup</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="rob-env" className="block text-xs font-medium text-slate-200 mb-1">
                          Operating Environment
                        </label>
                        <select
                          id="rob-env"
                          value={form.operatingEnvironment}
                          onChange={update("operatingEnvironment")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="Commercial Facility">Commercial Facility</option>
                          <option value="Industrial / Factory">Industrial / Factory</option>
                          <option value="R&D Lab / Educational">R&D Lab / Educational</option>
                          <option value="Consumer / Smart Device">Consumer / Smart Device</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Security Details */}
                {isSecurity && (
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl space-y-4 animate-fadeIn">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-light">
                      Security Details
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="sec-need" className="block text-xs font-medium text-slate-200 mb-1">
                          Primary Security Need
                        </label>
                        <select
                          id="sec-need"
                          value={form.securityNeed}
                          onChange={update("securityNeed")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="IP Surveillance & CCTV System">IP Surveillance & CCTV System</option>
                          <option value="Biometric Access Control">Biometric Access Control</option>
                          <option value="Network Firewall & Security Infrastructure">Network Firewall Infrastructure</option>
                          <option value="Physical Facility Security Audit">Physical Facility Security Audit</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="sec-scale" className="block text-xs font-medium text-slate-200 mb-1">
                          Facility Scale
                        </label>
                        <select
                          id="sec-scale"
                          value={form.facilityScale}
                          onChange={update("facilityScale")}
                          className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                        >
                          <option value="Small Office / Retail">Small Office / Retail</option>
                          <option value="Multi-Storey Commercial Building">Multi-Storey Commercial Building</option>
                          <option value="Industrial Plant / Campus">Industrial Plant / Campus</option>
                          <option value="Residential / Enterprise">Residential / Enterprise</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom Details */}
                {isCustom && (
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl space-y-4 animate-fadeIn">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-light">
                      Custom Scope
                    </p>
                    <div>
                      <label htmlFor="custom-cat" className="block text-xs font-medium text-slate-200 mb-1">
                        Scope Focus Area
                      </label>
                      <select
                        id="custom-cat"
                        value={form.customScopeCategory}
                        onChange={update("customScopeCategory")}
                        className="w-full px-3.5 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-primary"
                      >
                        <option value="Custom Hardware + Software Hybrid">Custom Hardware + Software Hybrid</option>
                        <option value="Enterprise Infrastructure & Systems Overhaul">Enterprise Infrastructure Overhaul</option>
                        <option value="Specialized Technical Support / AMC">Specialized Technical Support / AMC</option>
                        <option value="Other Custom Requirement">Other Custom Requirement</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* 5. Shared Details Textarea */}
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-slate-200 mb-1.5">
                    {isTraining
                      ? "Additional Details / Learning Goals *"
                      : "Project Details *"}
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    required
                    value={form.message}
                    onChange={update("message")}
                    className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm resize-y"
                    placeholder={
                      isTraining
                        ? "Tell us about your background, preferred schedule, or batch requirements..."
                        : "Describe your project goals, technical parameters, or custom requirements..."
                    }
                  ></textarea>
                </div>

                {status === "error" && (
                  <p className="text-red-400 text-sm font-medium">{errorMsg}</p>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full bg-primary hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-lg transition-all duration-200 shadow-lg shadow-primary/25 text-sm min-h-[48px] cursor-pointer"
                  >
                    {status === "sending" ? "Sending Inquiry..." : "Send Message"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
